import { Mountain, Download, Calendar, TrendingUp, MapPin, CloudRain } from 'lucide-react';
import FileUpload from './components/FileUpload';
import RouteInfo from './components/RouteInfo';
import RouteMap from './components/RouteMap';
import ElevationChart from './components/ElevationChart';
import AIAnalysis from './components/AIAnalysis';
import POIsPanel from './components/POIsPanel';
import StravaSegments from './components/StravaSegments';
import React, { useState, useRef } from 'react';
import SurfaceBar from './components/SurfaceBar';
import './App.css';

function App() {
  const [analysisData, setAnalysisData] = useState(null);
  const mapRef = useRef(null); // ← Referència pel mapa

  const handleFileSelect = (data) => {
    setAnalysisData(data);
  };

  const handleReset = () => {
    setAnalysisData(null);
  };

  const handleDownloadGPX = () => {
    if (!analysisData?.enhanced_gpx) return;

    const blob = new Blob([analysisData.enhanced_gpx], { 
      type: 'application/gpx+xml' 
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = analysisData.enhanced_gpx_filename || 'enhanced_route.gpx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <Mountain size={40} />
            <div>
              <h1>MTB Route Analyzer</h1>
              <p className="header-subtitle">Anàlisi professional de rutes amb IA, OSM i Strava</p>
            </div>
            {/* Botó per analitzar nova ruta */}
            {analysisData && (
              <button onClick={handleReset} className="header-new-analysis-button">
                ← Nova ruta
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="app-main">
        <div className="container">
          
          {/* File Upload Section */}
          {!analysisData && (
            <section className="upload-section">
              <FileUpload onAnalysisComplete={handleFileSelect} />
            </section>
          )}

          {/* Results Section */}
          {analysisData && (
            <>
              {/* Botó Download Sticky */}
              <button onClick={handleDownloadGPX} className="download-button-sticky">
                <Download size={20} />
                Descarregar GPX
              </button>

              {/* Route Info Card */}
              <section className="route-info-section">
                <RouteInfo 
                  filename={analysisData.filename}
                  targetDateTime={analysisData.target_datetime}
                />
              </section>

              {/* Stats Cards */}
              <section className="stats-section fade-in">
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <MapPin size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Distància</h3>
                      <p className="stat-value">{analysisData.route_stats.distance_km.toFixed(2)} km</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <TrendingUp size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Desnivell +</h3>
                      <p className="stat-value">{analysisData.route_stats.elevation_gain.toFixed(0)} m</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <Calendar size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Temps estimat</h3>
                      <p className="stat-value">{analysisData.ai_analysis.estimated_time}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <CloudRain size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Temperatura</h3>
                      <p className="stat-value">{analysisData.weather.start.temperature}°C</p>
                      <p className="stat-subtitle">{analysisData.weather.start.conditions}</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Map and Chart - AMB SINCRONITZACIÓ */}
              <section className="visualization-section fade-in">
                <div className="viz-grid">
                  <div className="viz-card map-card">
                    <h2>Mapa de la Ruta</h2>
                    <RouteMap 
                      ref={mapRef}
                      coordinates={analysisData.coordinates}
                      pois={analysisData.pois}
                      stravaSegments={analysisData.strava_segments}
                    />
                  </div>

                  <div className="viz-card chart-card">
                    <h2>Perfil d'Elevació</h2>
                    <ElevationChart 
                      coordinates={analysisData.coordinates}
                      mapRef={mapRef}
                    />
                  </div>
                </div>
              </section>
					
              {/* Surface Bar */}
              <section className="surface-section fade-in">
                <SurfaceBar surfaceData={analysisData.surface_data} />
              </section>

              {/* AI Analysis */}
              <section className="analysis-section fade-in">
                <AIAnalysis analysis={analysisData.ai_analysis} />
              </section>

              {/* POIs and Strava */}
              <section className="features-section fade-in">
                <div className="features-grid">
                  <POIsPanel pois={analysisData.pois} />
                  <StravaSegments segments={analysisData.strava_segments} />
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <p className="footer-title">MTB Route Analyzer</p>
              <p className="footer-text">
                Powered by Groq AI (Llama 3.3), Google Gemini, OpenStreetMap & Strava
              </p>
            </div>
            <div className="footer-section">
              <p className="footer-title">Contacte</p>
              <p className="footer-text">
                <a href="mailto:jordi@tecnojs.com" className="footer-link">
                  📧 jordi@tecnojs.com
                </a>
              </p>
            </div>
            <div className="footer-section">
              <p className="footer-copyright">
                © {new Date().getFullYear()} TecnoJS - Tots els drets reservats
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;